/**
 * [UTILITAIRE] Gestion des tokens d'invitation sécurisés
 * [RÔLE] Validation et récupération des informations d'invitation depuis un token
 * [SÉCURITÉ] Remplace l'exposition de l'email et process_id dans l'URL
 */

import { FamlaServices } from "../services";

/**
 * Interface pour les données d'invitation validées
 */
export interface InvitationTokenData {
    process_id: string;
    user_email: string;
    collaboration_id: string | null;
    expires_at: string;
    user_exists: boolean;  // Indique si l'utilisateur a un compte complet (avec mot de passe)
}

/**
 * Interface pour le résultat de validation du token
 */
export interface InvitationTokenResult {
    success: boolean;
    data: InvitationTokenData | null;
    error: string | null;
}

/**
 * Valide un token d'invitation et récupère les informations associées
 * @param {string} token - Token d'invitation à valider
 * @returns {Promise<InvitationTokenResult>}
 */
export const validateInvitationToken = async (token: string): Promise<InvitationTokenResult> => {
    try {
        if (!token || token.trim() === "") {
            return {
                success: false,
                data: null,
                error: "Token is required"
            };
        }

        console.log('[INVITATION TOKEN] Validating token:', token.substring(0, 8) + '...');
        
        // Appel à l'API pour valider le token
        const response = await FamlaServices("api_host", `/process/invitation/validate/${token}`, "GET", "", "");
        
        if (response.status === 200) {
            const data = response.body.data;
            console.log('[INVITATION TOKEN] Token validated successfully:', {
                process_id: data.process_id,
                user_email: data.user_email?.substring(0, 10) + '...',
                collaboration_id: data.collaboration_id
            });
            
            return {
                success: true,
                data: {
                    process_id: data.process_id,
                    user_email: data.user_email,
                    collaboration_id: data.collaboration_id,
                    expires_at: data.expires_at,
                    user_exists: data.user_exists !== undefined ? data.user_exists : true  // Par défaut true si non présent (rétrocompatibilité)
                },
                error: null
            };
        } else if (response.status === 404) {
            console.warn('[INVITATION TOKEN] Invalid token');
            return {
                success: false,
                data: null,
                error: "Invalid invitation token"
            };
        } else if (response.status === 410) {
            const description = response.body?.description || '';
            let errorMessage = "Cette invitation a expiré ou a déjà été utilisée.";
            
            if (description === 'expired' || description.includes('expired')) {
                errorMessage = "Cette invitation a expiré. Veuillez demander une nouvelle invitation.";
            } else if (description === 'already_used' || description.includes('already_used') || description.includes('already been used') || description.includes('already used')) {
                errorMessage = "Cette invitation a déjà été utilisée.";
            } else if (description === 'invalid' || description.includes('invalid')) {
                errorMessage = "Cette invitation n'est plus valide.";
            }
            
            console.warn('[INVITATION TOKEN] Token expired or already used:', description);
            return {
                success: false,
                data: null,
                error: errorMessage
            };
        } else {
            console.error('[INVITATION TOKEN] Error validating token:', response.status);
            return {
                success: false,
                data: null,
                error: "An error occurred while validating the invitation token"
            };
        }
    } catch (error: any) {
        console.error('[INVITATION TOKEN] Exception validating token:', error);
        return {
            success: false,
            data: null,
            error: error.message || "An error occurred while validating the invitation token"
        };
    }
};

/**
 * Extrait le token d'invitation depuis l'URL
 * @returns {string | null} Token d'invitation ou null si absent
 */
export const getInvitationTokenFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("invitation_token");
};
